import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PhoneIcon from '@mui/icons-material/Phone';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid2 as Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { getUiLanguage, tr } from '../lib/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import type { RequestDetails } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { Button } from '../components/button';
import { ConfirmDialog, ErrorState } from '../components/common';
import { DetailSkeleton } from '../components/skeletons';
import { AssignDialog, DetailHeader, InfoRow, NotesCard, RequestStatusChip, SectionCard, SourceChip } from '../components/org';
import { TextField } from '../components/text-field';
import { AttachmentsCard, FormAnswersCard } from '../components/EntryAnswers';
import { MatchingPropertiesCard } from '../components/MatchesCard';
import { useToast } from '../components/Toast';
import { apiLabel, fmtDate, range, REQUEST_STATUS } from '../lib/labels';
import { FeatureChips } from '../components/FeatureChips';

// "ASSIGNED" is set by the assignment action, not manually
const MANUAL_STATUSES = Object.keys(REQUEST_STATUS).filter((s) => s !== 'ASSIGNED');

export function RequestDetailsPage() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { me } = useAuth();
  const { toast, toastError } = useToast();
  const key = ['request', id];
  const q = useQuery({ queryKey: key, queryFn: () => api<RequestDetails>(`/requests/${id}`) });
  const [statusAnchor, setStatusAnchor] = useState<HTMLElement | null>(null);
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
  const [lostOpen, setLostOpen] = useState(false);
  const [lostReason, setLostReason] = useState('');
  const [assignOpen, setAssignOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: key });
    void qc.invalidateQueries({ queryKey: ['requests'] });
    void qc.invalidateQueries({ queryKey: ['stats'] });
  };
  const onConflict = (e: unknown) => {
    if (e instanceof ApiError && e.status === 409) {
      toast(tr('تم تعديل الطلب من مستخدم آخر — تم تحديث البيانات، أعد المحاولة', 'The request was changed by another user — data has been refreshed, please retry'), 'warning');
      refresh();
    } else toastError(e);
  };

  const status = useMutation({
    mutationFn: (v: { status: string; lostReason?: string }) => api(`/requests/${id}/status`, { method: 'POST', body: { ...v, version: q.data!.version } }),
    onSuccess: () => { toast(tr('تم تغيير الحالة', 'Status updated')); setLostOpen(false); setLostReason(''); refresh(); },
    onError: onConflict,
  });
  const assign = useMutation({
    mutationFn: (v: { toUserId: string | null; note: string }) => api(`/requests/${id}/assign`, { method: 'POST', body: { toUserId: v.toUserId, note: v.note || undefined, version: q.data!.version } }),
    onSuccess: () => { toast(tr('تم التعيين', 'Assignment updated')); setAssignOpen(false); refresh(); },
    onError: onConflict,
  });
  const del = useMutation({
    mutationFn: () => api(`/requests/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast(tr('تم حذف الطلب', 'Request deleted')); void qc.invalidateQueries({ queryKey: ['requests'] }); nav('/requests', { replace: true }); },
    onError: toastError,
  });

  if (q.isLoading) return <DetailSkeleton />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const r = q.data!;
  const closed = ['WON', 'LOST', 'CANCELLED'].includes(r.status);
  const pickStatus = (s: string) => {
    setStatusAnchor(null);
    if (s === 'LOST') setLostOpen(true);
    else status.mutate({ status: s });
  };

  return (
    <>
      <DetailHeader
        onBack={() => nav(-1)}
        title={`${apiLabel(r.request_type_name)}${r.property_type_name ? ` · ${apiLabel(r.property_type_name)}` : ''}`}
        badges={<><RequestStatusChip status={r.status} /><SourceChip source={r.source} /></>}
        meta={<><span dir="ltr" style={{ fontFamily: 'monospace', fontWeight: 600 }}>{r.ref_no}</span>{r.contact?.full_name && <> · {r.contact.full_name}</>}</>}
        actions={
          <>
            {r.can.changeStatus && <Button variant="contained" startIcon={<SwapHorizIcon />} onClick={(e) => setStatusAnchor(e.currentTarget)} loading={status.isPending}>{tr('تغيير الحالة', 'Change status')}</Button>}
            {((r.can.assign && !closed) || r.can.delete) && (
              <Tooltip title={tr('المزيد', 'More')}>
                <IconButton onClick={(e) => setMoreAnchor(e.currentTarget)} aria-label={tr('المزيد من الإجراءات', 'More actions')} sx={{ border: 1, borderColor: 'divider' }}>
                  <MoreVertIcon />
                </IconButton>
              </Tooltip>
            )}
          </>
        }
      />
      <Menu anchorEl={statusAnchor} open={!!statusAnchor} onClose={() => setStatusAnchor(null)}>
        {MANUAL_STATUSES.map((s) => (
          <MenuItem key={s} selected={s === r.status} disabled={s === r.status} onClick={() => pickStatus(s)} sx={{ minWidth: 220 }}>
            <ListItemIcon>{s === r.status ? <CheckIcon color="primary" /> : <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'divider' }} />}</ListItemIcon>
            <ListItemText primary={REQUEST_STATUS[s] ?? s} />
          </MenuItem>
        ))}
      </Menu>
      <Menu anchorEl={moreAnchor} open={!!moreAnchor} onClose={() => setMoreAnchor(null)}>
        {r.can.assign && !closed && (
          <MenuItem onClick={() => { setMoreAnchor(null); setAssignOpen(true); }}>
            <ListItemIcon><AssignmentIndIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary={r.assigned_to ? tr('إعادة تعيين الطلب', 'Reassign request') : tr('تعيين الطلب', 'Assign request')} />
          </MenuItem>
        )}
        {r.can.assign && !closed && r.can.delete && <Divider />}
        {r.can.delete && (
          <MenuItem onClick={() => { setMoreAnchor(null); setConfirmDelete(true); }} sx={{ color: 'error.main' }}>
            <ListItemIcon sx={{ color: 'error.main' }}><DeleteIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary={tr('حذف الطلب', 'Delete request')} />
          </MenuItem>
        )}
      </Menu>

      {r.is_possible_duplicate && <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>{tr('قد يكون هذا الطلب مكرراً — يوجد طلب مفتوح آخر لنفس رقم الهاتف.', 'This may be a duplicate — another open request uses the same phone number.')}</Alert>}
      {r.status === 'LOST' && r.lost_reason && <Alert severity="error" sx={{ mb: 2 }}>{tr('سبب الخسارة:', 'Loss reason:')} {r.lost_reason}</Alert>}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Stack spacing={2}>
            <SectionCard title={tr('ما يبحث عنه العميل', 'Customer requirements')}>
              <InfoRow label={tr('نوع الطلب', 'Request type')}>{apiLabel(r.request_type_name)}</InfoRow>
              <InfoRow label={tr('الفئة', 'Category')}>{apiLabel(r.category_name) ?? '—'}</InfoRow>
              <InfoRow label={tr('نوع العقار', 'Property type')}>{apiLabel(r.property_type_name) ?? '—'}</InfoRow>
              <InfoRow label={tr('الميزانية', 'Budget')}>{range(r.budget_min, r.budget_max, ` ${r.currency}`)}</InfoRow>
              {(r.area_min || r.area_max) && <InfoRow label={tr('المساحة', 'Area')}>{range(r.area_min, r.area_max, ' m²')}</InfoRow>}
              {(r.inner_area_min || r.inner_area_max) && <InfoRow label={tr('المساحة الداخلية', 'Interior area')}>{range(r.inner_area_min, r.inner_area_max, ' m²')}</InfoRow>}
              {(r.outer_area_min || r.outer_area_max) && <InfoRow label={tr('المساحة الخارجية', 'Exterior area')}>{range(r.outer_area_min, r.outer_area_max, ' m²')}</InfoRow>}
              {(r.land_area_min || r.land_area_max) && <InfoRow label={tr('مساحة الأرض', 'Land area')}>{range(r.land_area_min, r.land_area_max, ' m²')}</InfoRow>}
              {(r.building_area_min || r.building_area_max) && <InfoRow label={tr('مساحة البناء', 'Building area')}>{range(r.building_area_min, r.building_area_max, ' m²')}</InfoRow>}
              {r.rooms_min != null && <InfoRow label={tr('الغرف (حد أدنى)', 'Minimum rooms')}>{r.rooms_min}</InfoRow>}
              <InfoRow label={tr('المواقع المفضلة', 'Preferred locations')}>
                {r.locations.length ? (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end">
                    {r.locations.map((l) => <Chip key={l.id} size="small" label={[l.governorate_name, l.area_name, l.neighborhood_name].filter(Boolean).join(' / ')} />)}
                  </Stack>
                ) : '—'}
              </InfoRow>
              {r.features.length > 0 && (
                <Box sx={{ pt: 1.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{tr('المميزات المطلوبة', 'Required features')}</Typography>
                  <FeatureChips features={r.features} />
                </Box>
              )}
            </SectionCard>
            {r.direction !== 'SERVICE' && <MatchingPropertiesCard requestId={r.id} />}
            <FormAnswersCard fields={r.form_fields ?? []} answers={r.submission?.answers} currency={r.currency} />
            <AttachmentsCard files={r.files ?? []} fields={r.form_fields ?? []} edit={r.can.update ? { path: `/requests/${r.id}`, onChanged: refresh } : undefined} />
            <NotesCard entityType="REQUEST" entityId={r.id} />
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <Stack spacing={2}>
            <SectionCard title={tr('العميل', 'Customer')}>
              {r.contact ? (
                <>
                  <InfoRow label={tr('الاسم', 'Name')}>{r.contact.full_name}</InfoRow>
                  <InfoRow label={tr('الهاتف', 'Phone')}><span dir="ltr">{r.contact.phone}</span></InfoRow>
                  {r.contact.email && <InfoRow label={tr('البريد', 'Email')}>{r.contact.email}</InfoRow>}
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }}>
                    <Button size="small" variant="outlined" startIcon={<PhoneIcon />} href={`tel:${r.contact.phone}`}>{tr('اتصال', 'Call')}</Button>
                    <Button size="small" variant="outlined" color="success" startIcon={<WhatsAppIcon />} href={`https://wa.me/${r.contact.phone.replace(/\D/g, '')}`} target="_blank">{tr('واتساب', 'WhatsApp')}</Button>
                  </Stack>
                </>
              ) : '—'}
            </SectionCard>
            <SectionCard title={tr('المتابعة', 'Follow-up')}>
              <InfoRow label={tr('المسؤول', 'Assignee')}>{r.assigned_to === me?.user.id ? tr('أنا', 'Me') : r.assigned_to_name ?? tr('غير معيّن', 'Unassigned')}</InfoRow>
              <InfoRow label={tr('أدخله', 'Created by')}>{r.created_by_name ?? tr('العميل (رابط عام)', 'Customer (public link)')}</InfoRow>
              <InfoRow label={tr('تاريخ الطلب', 'Request date')}>{fmtDate(r.created_at)}</InfoRow>
              <InfoRow label={tr('تاريخ التعيين', 'Assigned at')}>{fmtDate(r.assigned_at)}</InfoRow>
              <InfoRow label={tr('أول تواصل', 'First contact')}>{fmtDate(r.first_contact_at)}</InfoRow>
            </SectionCard>
            <SectionCard title={tr('سجل التعيين', 'Assignment history')}>
              {r.assignments.length === 0 ? <Typography color="text.secondary" variant="body2">{tr('لم يُعيَّن بعد', 'Not assigned yet')}</Typography> : (
                <Stack spacing={1.25}>
                  {r.assignments.map((a) => (
                    <Box key={a.id} sx={{ borderInlineStart: 3, borderColor: 'primary.main', pl: 1.5 }}>
                      <Typography variant="body2">
                        {a.from_name ?? tr('غير معيّن', 'Unassigned')} {getUiLanguage() === 'ar' ? '←' : '→'} <b>{a.to_name ?? tr('غير معيّن', 'Unassigned')}</b>
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{tr('بواسطة', 'By')} {a.by_name} · {fmtDate(a.created_at)}</Typography>
                      {a.note && <Typography variant="body2" color="text.secondary">«{a.note}»</Typography>}
                    </Box>
                  ))}
                </Stack>
              )}
            </SectionCard>
          </Stack>
        </Grid>
      </Grid>

      {assignOpen && <AssignDialog open title={tr(`تعيين الطلب ${r.ref_no}`, `Assign request ${r.ref_no}`)} currentUserId={r.assigned_to} busy={assign.isPending} onClose={() => setAssignOpen(false)} onSubmit={(toUserId, note) => assign.mutate({ toUserId, note })} />}
      <Dialog open={lostOpen} onClose={() => setLostOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{tr('إغلاق الطلب كخاسر', 'Close request as lost')}</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth multiline minRows={2} label={tr('سبب الخسارة', 'Loss reason')} value={lostReason} onChange={(e) => setLostReason(e.target.value)} sx={{ mt: 1 }} helperText={tr('مثال: وجد عقاراً عند مكتب آخر، الميزانية غير كافية…', 'Example: found another property, insufficient budget…')} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLostOpen(false)}>{tr('إلغاء', 'Cancel')}</Button>
          <Button variant="contained" color="error" disabled={lostReason.trim().length < 2} loading={status.isPending} onClick={() => status.mutate({ status: 'LOST', lostReason: lostReason.trim() })}>{tr('تأكيد', 'Confirm')}</Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog open={confirmDelete} danger title={tr('حذف الطلب', 'Delete request')} text={tr(`سيتم حذف الطلب ${r.ref_no}.`, `Request ${r.ref_no} will be deleted.`)} confirmLabel={tr('حذف', 'Delete')} busy={del.isPending} onConfirm={() => del.mutate()} onClose={() => setConfirmDelete(false)} />
    </>
  );
}
